import type { StatusProps } from '@main/complykit/components/status';
import type { LabelColor } from '@main/complykit/tokens/colors';

export type StatusTone = StatusProps['tone'];

/**
 * Bridges from the legacy Chakra `colorScheme` axis onto the two ComplyKit
 * color axes (docs/specs/frontend/design-system/badge-status-tag.md).
 *
 * The chip engines (EditableStatus, EditableTag, Card.Tags) render ComplyKit
 * Status/Tag, but many call sites still describe color as a Chakra scheme.
 * These maps apply the generic collapses; the per-status deliberate ones
 * (In progress -> info, Avoided -> neutral, VQ Created -> neutral, ...) live
 * next to the status maps and arrive as an explicit `tone` on the option.
 */
const COLOR_SCHEME_TONE: Record<string, StatusTone> = {
  gray: 'neutral',
  blackAlpha: 'neutral',
  whiteAlpha: 'neutral',
  /* The one purple status (treatment Accepted) collapses to neutral. */
  purple: 'neutral',
  green: 'success',
  orange: 'warning',
  /* Warning is orange, not yellow; the yellow maps were the drift. */
  yellow: 'warning',
  red: 'critical',
  blue: 'info',
  /* Progress states were scattered across teal and cyan; they are info. */
  teal: 'info',
  cyan: 'info',
};

export function toneFromColorScheme(colorScheme: string | undefined): StatusTone {
  return (colorScheme && COLOR_SCHEME_TONE[colorScheme]) || 'neutral';
}

const LABEL_COLORS = new Set<string>([
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'cyan',
  'blue',
  'indigo',
  'purple',
  'pink',
  'brown',
  'gray',
]);

/**
 * A Chakra scheme that names one of the twelve label colors maps onto it;
 * anything else (blackAlpha, linkedin, ...) falls back to the gray mute,
 * which is also what an absent color renders as.
 */
export function labelColorFromColorScheme(colorScheme: string | undefined): LabelColor | undefined {
  return colorScheme && LABEL_COLORS.has(colorScheme) ? (colorScheme as LabelColor) : undefined;
}
